import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";

import Bubble, { Markdown } from "../components/Bubble";
import Attachment from "../components/Attachment";
import MenuButtons from "../components/MenuButtons";
import ListPreview from "../components/ListPreview";
import ListModal from "../components/Modals/ListModal";
import ImageModal from "../components/Modals/ImageModal";

import api from "../functions/api";
import { useToast } from "../components/ToastProvider";
import useAutosizeTextarea from "../hooks/useAutosizeTextarea";
import "../styles/Chat.css";
import { ReactComponent as IconClip } from "../assets/icons/icon-clip.svg";
import { ReactComponent as IconArrow } from "../assets/icons/icon-arrow.svg";

export default function ChatPage() {
  const { uuid_thread } = useParams();
  const navigate = useNavigate();
  const navigatingRef = useRef(false);

  // Estado de la conversación
  const [thread, setThread] = useState(null);       // siempre será un UUID
  const [threadInfo, setThreadInfo] = useState(null);

  // Mensajes
  const [msgs, setMsgs] = useState([]);
  const [files, setFiles] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  // Estado WS
  const [wsStatus, setWsStatus] = useState("connecting"); // connecting | connected | reconnecting | disconnected
  const [wsAttempts, setWsAttempts] = useState(0);

  const { showError } = useToast();

  // refs
  const taRef = useRef(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const lastBot = useRef(null);
  const wsRef = useRef(null);
  const lastThoughtId = useRef(null);
  const streamMapRef = useRef(new Map());
  const lastToastRef = useRef(0);

  useEffect(() => {
    const h = () => { navigatingRef.current = true; };
    window.addEventListener("chat:route-change", h);
    return () => window.removeEventListener("chat:route-change", h);
  }, []);

  // Dedupe de eventos (firma -> timestamp)
  const seenRef = useRef(new Map());
  const isDup = (sig, ttlMs = 2000) => {
    const now = Date.now();
    // Limpieza de llaves viejas
    for (const [k, t] of seenRef.current) {
      if (now - t > ttlMs) seenRef.current.delete(k);
    }
    if (seenRef.current.has(sig)) return true;
    seenRef.current.set(sig, now);
    return false;
  };

  // Heartbeat (callbacks se reemplazan por conexión)
  const heartbeatRef = useRef({ onPong: null });

  useAutosizeTextarea(taRef, input);

  // scroll al final
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  // Cargar histórico de un uuid
  const loadHistory = useCallback(async (uuidThread) => {
    setMsgs([]); // limpia mensajes al cambiar
    try {
      const token = localStorage.getItem("access_token");
      const idUser = localStorage.getItem("id_user");

      const r = await api.get(`/langgraph/threads/${idUser}/history/${uuidThread}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status !== 200) return;

      const d = r.data;
      if (!d || !d.messages) return;

      const grouped = [];
      let currentThought = null;

      d.messages.forEach((m) => {
        const role = (m.role || "").toLowerCase();
        const isSys = role.includes("system") || role.includes("tool");
        const isHum = role.includes("human");

        if (isSys) {
          if (currentThought) {
            currentThought.content += (currentThought.content ? "\n" : "") + (m.content ?? "");
          } else {
            currentThought = {
              id: uuid(),
              role: "assistant",
              content: m.content ?? "",
              messageType: "thought",
              collapsed: false,
            };
          }
          return;
        }

        if (currentThought) {
          grouped.push(currentThought);
          currentThought = null;
        }

        grouped.push({
          id: uuid(),
          role: isHum ? "user" : "assistant",
          content: m.content ?? "",
          messageType: "text",
        });
      });

      if (currentThought) grouped.push(currentThought);
      setMsgs(grouped);
    } catch {
      /* noop */
    }
  }, []);

  // Montaje / cuando cambia uuid_thread en la URL
  useEffect(() => {
    const idUser = localStorage.getItem("id_user");

    // cada vez que cambia la ruta, reseteamos identificadores de stream y de último bot
    lastBot.current = null;
    lastThoughtId.current = null;
    streamMapRef.current = new Map();

    if (uuid_thread) {
      // buscamos info de todas y seleccionamos por uuid
      const fetchThreadInfo = async () => {
        try {
          const token = localStorage.getItem("access_token");
          const r = await api.get(`/langgraph/threads/${idUser}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const t = (r.data || []).find((th) => String(th.uuid) === String(uuid_thread));
          if (t) {
            // fijamos *ya* el uuid actual para que cualquier envío posterior lo use bien
            setThread(t.uuid);
            setThreadInfo(t);
            await loadHistory(t.uuid);
          } else {
            // si no existe ese uuid en BBDD, vete al default
            window.dispatchEvent(new Event("chat:route-change"));
            navigate("/chat");
          }
        } catch {
          showError("No se pudo cargar la conversación");
        }
      };
      fetchThreadInfo();
    } else {
      // obtener la default si no hay uuid en URL
      const fetchDefaultThread = async () => {
        try {
          const token = localStorage.getItem("access_token");
          const r = await api.get(`/langgraph/threads/${idUser}/default-thread`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r.status === 200 && r.data) {
            setThread(r.data.uuid);
            setThreadInfo(r.data);
            await loadHistory(r.data.uuid);
            window.dispatchEvent(new Event("chat:route-change"));
            navigate(`/chat/${r.data.uuid}`, { replace: true });
          }
        } catch {
          showError("No se pudo cargar la conversación por defecto");
        }
      };
      fetchDefaultThread();
    }
  }, [uuid_thread, loadHistory, showError, navigate]);

  // WebSocket + Heartbeat. Se re-crea cuando cambia `thread`
  useEffect(() => {
    if (!thread) return;

    // ——— cierre del WS anterior antes de abrir uno nuevo ———
    const closeExisting = (reason = "route-change") => {
      const ws = wsRef.current;
      if (!ws) return;
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      try { ws.close(1000, reason); } catch {}
      wsRef.current = null;
    };
    const prev = wsRef.current;
    closeExisting("route-change");

    const waitPrevClosed = async () => {
      if (!prev) return;
      const start = Date.now();
      while (prev.readyState !== WebSocket.CLOSED && Date.now() - start < 300) {
        await new Promise(r => setTimeout(r, 25));
      }
    };

    const gen = (wsRef.current?._gen || 0) + 1; // generación de esta conexión
    let retry = 0;
    let reconnectTimer = null;

    setWsStatus("connecting");
    setWsAttempts(0);

    // Heartbeat timers (por conexión)
    let hbTimer = null;
    let hbTimeout = null;
    const HB_INTERVAL = 20000; // ms
    const HB_GRACE = 10000;    // ms

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };
    const clearHB = () => {
      if (hbTimer) { clearInterval(hbTimer); hbTimer = null; }
      if (hbTimeout) { clearTimeout(hbTimeout); hbTimeout = null; }
    };

    const connect = () => {
      const token = localStorage.getItem("access_token");
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const host = window.location.host;
      const url = `${protocol}//${host}/langgraph/ws/listen/${thread}`;

      const ws = new WebSocket(url, [token]);
      ws._gen = gen;
      ws._thread = thread; // etiqueta de seguridad
      wsRef.current = ws;

      // registrar callback de pong de esta conexión
      heartbeatRef.current.onPong = () => {
        if (hbTimeout) {
          clearTimeout(hbTimeout);
          hbTimeout = null;
        }
      };

      ws.onopen = () => {
        navigatingRef.current = false;
        // si la conexión no corresponde al thread actual, ciérrala
        if (ws._thread !== thread) {
          try { ws.close(1000, "stale-thread"); } catch {}
          return;
        }
        retry = 0;
        setWsStatus("connected");
        setWsAttempts(0);

        clearHB();
        hbTimer = setInterval(() => {
          if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
          try {
            wsRef.current.send(JSON.stringify({ type: "ping", t: Date.now() }));
            if (hbTimeout) clearTimeout(hbTimeout);
            hbTimeout = setTimeout(() => {
              try { wsRef.current?.close(4000, "heartbeat-timeout"); } catch {}
            }, HB_GRACE);
          } catch {}
        }, HB_INTERVAL);
      };

      ws.onmessage = (ev) => {
        // descarta frames de un socket obsoleto
        if (ws !== wsRef.current || ws._gen !== gen || ws._thread !== thread) return;
        if (!ev.data) return;

        let payload;
        try { payload = JSON.parse(ev.data); }
        catch { payload = { type: "text", content: ev.data }; }

        // Heartbeat (PONG)
        if (payload?.type === "pong") {
          heartbeatRef.current?.onPong?.(payload);
          return;
        }

        // DEDUPE: evita dobles entregas idénticas durante reconexiones / solapes WS ---
        // Creamos una firma robusta por tipo. Para streams usamos handle; para texto, longitud+un trocito.
        const sig =
          payload?.type === "stream_patch" || payload?.type === "stream_start" || payload?.type === "stream_end"
            ? `${payload.type}:${payload.handle || "nohandle"}:${(payload.content || payload.delta || "").length}`
            : payload?.type === "attachment"
            ? `${payload.type}:${(payload.caption || "").slice(0,32)}:${(payload.fileb64 || "").length}`
            : payload?.type === "thought" || payload?.type === "text" || payload?.type === "error" || payload?.type === "menu"
            ? `${payload.type}:${(payload.content || payload.prompt || "").slice(0,64)}:${(payload.content || "").length}`
            : `raw:${ev.data.slice(0,64)}:${ev.data.length}`;
        if (isDup(sig)) return;

        setMsgs((prev) => {
          let draft = [...prev];

          const collapseLastThought = () => {
            if (lastThoughtId.current) {
              draft = draft.map((m) =>
                m.id === lastThoughtId.current ? { ...m, collapsed: true } : m
              );
              lastThoughtId.current = null;
            }
          };

          switch (payload.type) {
            case "thought": {
              if (lastThoughtId.current) {
                draft = draft.map((m) =>
                  m.id === lastThoughtId.current
                    ? { ...m, content: (m.content || "") + "\n" + (payload.content || "") }
                    : m
                );
              } else {
                const id = uuid();
                lastThoughtId.current = id;
                draft.push({
                  id,
                  role: "assistant",
                  content: payload.content,
                  messageType: "thought",
                  collapsed: false,
                });
              }
              return draft;
            }
            case "error": {
              collapseLastThought();
              draft.push({
                id: uuid(),
                role: "assistant",
                content: payload.content,
                messageType: "error",
              });
              return draft;
            }
            case "attachment": {
              collapseLastThought();
              const mime = payload.mime_type || "application/octet-stream";
              const dataUrl = `data:${mime};base64,${payload.fileb64}`;
              draft.push({
                id: uuid(),
                role: "assistant",
                attachment: dataUrl,
                mime,
                caption: payload.caption,
              });
              return draft;
            }
            case "menu": {
              collapseLastThought();
              if (payload.style === "list") {
                draft.push({
                  id: uuid(),
                  role: "assistant",
                  list: {
                    prompt: payload.prompt,
                    button: payload.button_label || "Ver opciones",
                    options: payload.options,
                  },
                });
              } else {
                draft.push({ id: uuid(), role: "assistant", menu: payload });
              }
              return draft;
            }
            case "stream_start": {
              const id = uuid();
              streamMapRef.current.set(payload.handle, id);
              collapseLastThought();
              draft.push({
                id,
                role: "assistant",
                content: payload.initial ?? "",
                messageType: "text",
              });
              return draft;
            }
            case "stream_patch": {
              let id = streamMapRef.current.get(payload.handle);
              if (!id) {
                id = uuid();
                streamMapRef.current.set(payload.handle, id);
                draft.push({ id, role: "assistant", content: "", messageType: "text" });
              }
              const content = (payload.content ?? payload.delta ?? "").toString();
              draft = draft.map((m) => (m.id === id ? { ...m, content } : m));
              return draft;
            }
            case "stream_end": {
              streamMapRef.current.delete(payload.handle);
              return draft;
            }
            case "text":
            default: {
              collapseLastThought();
              const content = (payload.content ?? "").toString();
              if (!lastBot.current) {
                lastBot.current = uuid();
                draft.push({ id: lastBot.current, role: "assistant", content: "" });
              }
              draft = draft.map((m) =>
                m.id === lastBot.current
                  ? { ...m, content: (m.content || "") + content }
                  : m
              );
              return draft;
            }
          }
        });
      };

      ws.onclose = (ev) => {
        clearHB();

        // conexión vieja → ignorar
        if (ws !== wsRef.current || ws._gen !== gen || ws._thread !== thread) return;

        if (navigatingRef.current) return;

        // cierres previstos → no reconectar
        if (ev.code === 1000 || ev.reason === "cleanup" || ev.reason === "route-change") {
          setWsStatus("disconnected");
          return;
        }

        // auth inválida → logout
        if (ev.code === 1008) {
          setWsStatus("disconnected");
          try {
            localStorage.removeItem("access_token");
            localStorage.removeItem("id_user");
            localStorage.removeItem("full_name");
          } catch {}
          window.location.href = "/login?error=sessionExpired";
          return;
        }

        // anómalo → reintenta
        const now = Date.now();
        if (now - lastToastRef.current > 10000) {
          lastToastRef.current = now;
          showError("Conexión perdida. Reintentando…");
        }
        setWsStatus("reconnecting");
        const delay = Math.min(10000, 500 * 2 ** retry++);
        setWsAttempts(retry);
        clearReconnectTimer();
        reconnectTimer = setTimeout(() => {
          // reintenta sólo si no hay una conexión nueva
          if (wsRef.current == null || wsRef.current._gen === gen) {
            connect();
          }
        }, delay);
      };

      ws.onerror = () => {
        // onclose gestiona el backoff
      };
    };

    // arranca solo cuando el anterior terminó de cerrarse (evita 2 WS solapados)
    (async () => {
      await waitPrevClosed();
      connect();
    })();

    // cleanup cuando cambia `thread` o desmonta
    return () => {
      clearReconnectTimer();
      clearHB();
      const ws = wsRef.current;
      if (ws && ws._gen === gen) {
        try { ws.close(1000, "route-change"); } catch {}
        wsRef.current = null;
      }
    };
  }, [thread, showError]);

  // Cierre limpio al abandonar pestaña
  useEffect(() => {
    const h = () => {
      try { wsRef.current?.close(1000, "cleanup"); } catch {}
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  // Adjuntos
  const handleFileChange = (e) =>
    setFiles(e.target.files ? Array.from(e.target.files) : []);
  const removeFile = (f) => setFiles((prev) => prev.filter((x) => x !== f));
  const resetFiles = () => {
    setFiles([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Envío
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // snapshot del thread actual: evita fugas si cambia justo al hacer submit
      const threadSnapshot = thread;

      if (!threadSnapshot || sending || (!input.trim() && files.length === 0)) return;
      setSending(true);

      // adjuntos
      if (files.length) {
        const fd = new FormData();
        fd.append("thread", threadSnapshot); // uuid
        files.forEach((f) => fd.append("files", f, f.name));
        try {
          const token = localStorage.getItem("access_token");
          await api.post("/langgraph/upload", fd, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch {
          showError("Error al subir el archivo");
        }

        setMsgs((p) => [
          ...p,
          ...files.map((f) => ({
            id: uuid(),
            role: "user",
            attachment: URL.createObjectURL(f),
            mime: f.type || "application/octet-stream",
            caption: f.name,
          })),
        ]);
        resetFiles();
      }

      try {
        const text = input.trim();
        if (text) setMsgs((p) => [...p, { id: uuid(), role: "user", content: text }]);
        setInput("");

        const token = localStorage.getItem("access_token");
        const full_name = localStorage.getItem("full_name");
        await api.post(
          "/langgraph/ws/publish",
          { thread: threadSnapshot, user_input: text, username: full_name },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setSending(false);
      } catch {
        showError("Error al enviar el mensaje");
        setSending(false);
      }
    },
    [thread, sending, input, files, showError]
  );

  // Modales
  const [modalData, setModalData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalImg, setModalImg] = useState(null);

  const openListModal = (list) => {
    setModalData(list);
    setShowModal(true);
  };
  const chooseRow = (row) => {
    setMsgs((p) => [...p, { id: uuid(), role: "user", content: row.title }]);
    setShowModal(false);
  };
  const closeModal = () => setShowModal(false);
  const toggleCollapse = (id) =>
    setMsgs((p) => p.map((m) => (m.id === id ? { ...m, collapsed: !m.collapsed } : m)));
  const openImage = (src, caption) => setModalImg({ src, caption });
  const closeImage = () => setModalImg(null);

  const inputDisabled = !thread || sending || wsStatus !== "connected";

  return (
    <section className="chatbot-section flex flex-col h-[calc(100vh-56px)]">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="title">{threadInfo?.name || "Asistente Virtual"}</h1>
        <div className="text-sm">
          {wsStatus === "connected" && <span className="text-green-600">Conectado</span>}
          {wsStatus === "connecting" && <span className="text-gray-500">Conectando…</span>}
          {wsStatus === "reconnecting" && (
            <span className="text-amber-600">
              Reconectando{wsAttempts ? ` (intento ${wsAttempts})` : ""}…
            </span>
          )}
          {wsStatus === "disconnected" && <span className="text-red-600">Desconectado</span>}
        </div>
      </div>

      <div className={`py-6 space-y-3 ${msgs.length ? "flex-1 overflow-y-auto" : "flex-none"}`}>
        {msgs.map((m) => (
          <Bubble
            key={m.id}
            role={m.role}
            messageType={m.messageType}
            collapsed={m.collapsed}
            onToggle={m.messageType === "thought" ? () => toggleCollapse(m.id) : undefined}
          >
            {m.content &&
              (m.role === "assistant" ? <Markdown>{m.content}</Markdown> : m.content)}
            <Attachment
              role={m.role}
              attachment={m.attachment}
              mime={m.mime}
              caption={m.caption}
              onOpenImage={openImage}
            />
            {m.menu && <MenuButtons menu={m.menu} onChoose={() => {}} />}
            {m.list && <ListPreview list={m.list} onOpen={openListModal} />}
          </Bubble>
        ))}

        {sending && (
          <Bubble role="assistant">
            <span className="animate-pulse">…</span>
          </Bubble>
        )}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        className={`chat-input w-full px-4 py-3 ${msgs.length ? "sticky bottom-0 bg-white" : "my-auto"}`}
      >
        <div className="flex w-full justify-center">
          <div className="chat-input-text relative w-full max-w-2xl rounded-2xl border shadow px-4 py-2 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="icon-btn text-gray-400 fs-5"
                disabled={inputDisabled}
              >
                <IconClip className="mc-link__icon secondary" aria-label="Adjuntar archivo" />
              </button>

              <textarea
                ref={taRef}
                rows={1}
                placeholder={wsStatus === "connected" ? "Escribe tu mensaje…" : "Conectando…"}
                className="flex-grow resize-none overflow-hidden bg-transparent focus:outline-none px-3"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    const form = e.currentTarget.form;
                    if (form) {
                      form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
                    }
                  }
                }}
                disabled={inputDisabled}
              />

              <button
                type="submit"
                disabled={(!input.trim() && files.length === 0) || inputDisabled}
                className="composer-submit-btn h-9 w-9 flex items-center justify-center"
                title={wsStatus !== "connected" ? "Esperando conexión…" : "Enviar"}
              >
                <IconArrow className="mc-link__icon secondary" aria-label="Enviar mensaje" />
              </button>
            </div>

            {files.length > 0 && (
              <ul className="flex flex-wrap gap-2 text-xs text-gray-700">
                {files.map((f) => (
                  <li key={f.name} className="border rounded px-2 py-1 bg-gray-100 flex items-center gap-1">
                    <i className="bi bi-file-earmark" />
                    <span className="truncate max-w-[150px]">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(f)}
                      className="text-red-600 hover:text-red-800 ms-1"
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <input ref={fileRef} type="file" hidden multiple onChange={handleFileChange} />
      </form>

      <ListModal open={showModal && !!modalData} list={modalData} onClose={closeModal} onChoose={chooseRow} />
      <ImageModal src={modalImg?.src} caption={modalImg?.caption} onClose={closeImage} />
    </section>
  );
}
